"use client";
import React, { useMemo } from "react";

import { strings } from "@perceptron/constants";
import { cn } from "@perceptron/utils/cn";
import { MediaKind } from "@perceptron/utils/types";
import Image from "next/image";

import { TermsAndPrivacyDisclaimer } from "@demo/components/TermsAndPrivacyDisclaimer";
import AgentOutput from "@demo/features/agent-output";
import { useDevModeActivation } from "@demo/hooks/useDevModeActivation";
import { useDevTestData } from "@demo/hooks/useDevTestData";
import { useHasRunOutput } from "@demo/hooks/useHasRunOutput";
import { useIsMobile } from "@demo/hooks/useIsMobile";
import { useSearchToolsActivation } from "@demo/hooks/useSearchToolsActivation";
import { MediaFactory } from "@demo/libs/media-factory";
import { useDisplayedMediaIndex, useMediaCarouselStore } from "@demo/store/media-carousel";
import { AgentState, useModelStore } from "@demo/store/model";

import { AssetFade } from "./AssetFade";
import { LAYOUT_BREAKPOINT } from "./constants";
import ImageOutput from "./ImageOutput";
import { OutputFilmstrip } from "./OutputFilmstrip";
import { useBackgroundWarming } from "./useBackgroundWarming";
import { useFollowStreamingAsset } from "./useFollowStreamingAsset";
import VideoOutput from "./VideoOutput";
import LoadingDots from "../agent-output/LoadingDots";
import { RAIL_SIZE } from "../media/FilmstripRail";

const ComposeOutput = () => {
  useDevModeActivation();
  useDevTestData();
  useSearchToolsActivation();
  useFollowStreamingAsset();
  const { onActivePlayerBusyChange } = useBackgroundWarming();
  const media = useMediaCarouselStore((state) => state.mediaInAgentCarousel);
  const hasRunOutput = useHasRunOutput();
  const agentState = useModelStore((state) => state.agentState);
  const isEmptyResponse = useModelStore((state) => state.isEmptyResponse);

  const currentMediaIndex = useDisplayedMediaIndex();
  const currentMedia = useMemo(() => media[currentMediaIndex], [media, currentMediaIndex]);
  const currentMediaKind = useMemo(
    () => (currentMedia ? MediaFactory.detectMediaKind(currentMedia) : undefined),
    [currentMedia],
  );

  const isMobile = useIsMobile(LAYOUT_BREAKPOINT);
  const showRail = media.length > 1;

  const displayAgentOutput = useMemo(() => {
    // The whole run, not just evaluating: the first token resets the output and moves to streaming a frame
    // before its text commits, and hiding for that frame restarts the reveal.
    const isRunning = agentState === AgentState.EVALUATING || agentState === AgentState.STREAMING;

    return !!media.length && (isRunning || hasRunOutput || isEmptyResponse);
  }, [agentState, hasRunOutput, isEmptyResponse, media.length]);

  if (!displayAgentOutput) {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center rounded-2xl bg-black">
        <div className="relative mb-4 h-20 w-24">
          <div>
            <Image
              alt={strings.agentOutput.logo}
              className="h-20 w-24"
              height={80}
              priority
              src="/demo/assets/logo-light.svg"
              width={96}
            />
          </div>
        </div>
        <div className="font-ppmondwest text-[24px] text-white">
          <LoadingDots agentState={agentState} />
        </div>
        <TermsAndPrivacyDisclaimer className="absolute bottom-4 left-0 right-0 hidden opacity-100 transition-opacity duration-300 sm:block" />
      </div>
    );
  }

  if (currentMediaKind === MediaKind.Video) {
    // The rail lives here rather than inside VideoOutput so it stays on screen
    // independently of the player, including before a source has loaded.
    return (
      // `bg-black` matters on desktop: the rail is translucent glass docked to
      // the panel's left edge, so it needs the panel behind it — without it the
      // page background shows through the rail and the gutter beside the
      // player's frame, and the two read as separate floating panels.
      //
      // Not on mobile. There the player and the "Show response" bar are
      // separate cards stacked with a gap, and a black wrapper fills that gap
      // and fuses them into one slab. The gap should be whatever the page
      // background is, so it is left unpainted.
      <div className="relative h-full overflow-hidden rounded-2xl sm:bg-black">
        <AssetFade key={currentMedia?.key}>
          <VideoOutput
            insetLeft={showRail && !isMobile ? RAIL_SIZE : 0}
            insetTop={showRail && isMobile ? RAIL_SIZE : 0}
            onTrackingBusyChange={onActivePlayerBusyChange}
          />
        </AssetFade>
        <OutputFilmstrip orientation={isMobile ? "horizontal" : "vertical"} />
      </div>
    );
  }

  return (
    <div className="relative flex h-full flex-col">
      <ImageOutput />
      <div
        className={cn("absolute bottom-0 block w-full sm:hidden", "transition-all duration-500 ease-in-out", {
          "h-[calc(50%-16px)] opacity-100": displayAgentOutput,
          "h-0 opacity-0": !displayAgentOutput,
        })}
      >
        <AgentOutput />
      </div>
    </div>
  );
};

export default ComposeOutput;
